import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/unifiedUserService';
import { s3PhotoService } from '../services/s3PhotoService';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import StateLicensesCard from '../components/StateLicensesCard';
import { Camera, Trash2, User } from 'lucide-react';

const profileSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  state: yup.string(),
  certifyingBody: yup.string().required('Certifying body is required'),
  membershipType: yup.string(),
  memberNumber: yup.string(),
  currentCycleEnd: yup.string().required('CEU cycle end date is required'),
  currentCycleStart: yup.string()
});

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const MEMBERSHIP_TYPES = {
  RID: ['CI', 'CT', 'CDI', 'CI/CT', 'NIC', 'NIC Advanced', 'NIC Master', 'SC:L', 'OTC', 'Ed:K-12', 'CLIP-R'],
  BEI: ['Level I', 'Level II', 'Level III', 'Level IV', 'Specialist', 'Master']
};

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      state: '',
      certifyingBody: 'RID',
      membershipType: '',
      memberNumber: '',
      currentCycleEnd: new Date(new Date().setFullYear(new Date().getFullYear() + 4)).toISOString().split('T')[0],
      currentCycleStart: ''
    }
  });

  const watchedCertifyingBody = watch('certifyingBody');

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadProfile = async () => {
    if (!user?.userId) {
      console.warn('Profile loading skipped - no userId available');
      return;
    }
    
    setProfileLoading(true);
    setError(''); // Clear any previous errors
    
    try {
      console.log('🔍 Loading profile for userId:', user.userId);
      const result = await userService.getUser(user.userId);
      
      if (result && result.success && result.data) {
        const data = result.data;
        console.log('✅ Profile loaded successfully:', { 
          firstName: data.firstName, 
          lastName: data.lastName, 
          hasState: !!data.state,
          profileCompleted: data.profileCompleted 
        });
        
        const cycleEnd = data.currentCycleEnd || new Date(new Date().setFullYear(new Date().getFullYear() + 4)).toISOString().split('T')[0];
        
        reset({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          state: data.state || '',
          certifyingBody: data.certifyingBody || 'RID',
          membershipType: data.membershipType || '',
          memberNumber: data.memberNumber || '',
          currentCycleEnd: cycleEnd,
          currentCycleStart: data.currentCycleStart || ''
        });
        
        // Load photo URL if user has a photo
        if (data.profilePhotoUrl) {
          const photoUrl = await s3PhotoService.getPhotoUrl(user.userId);
          if (photoUrl) {
            setProfilePhoto(photoUrl);
          }
        }
      } else if (result && !result.success) {
        // Profile doesn't exist yet - this is normal for new users
        console.log('ℹ️ Profile not found for new user, using defaults:', result.error);
        
        // Initialize form with default values for new user
        const defaultCycleEnd = new Date(new Date().setFullYear(new Date().getFullYear() + 4)).toISOString().split('T')[0];
        
        reset({
          firstName: user.attributes?.given_name || '',
          lastName: user.attributes?.family_name || '',
          state: '',
          certifyingBody: 'RID',
          membershipType: '',
          memberNumber: '',
          currentCycleEnd: defaultCycleEnd,
          currentCycleStart: ''
        });
        
        // Show helpful message for new users
        setSuccess('Welcome! Please complete your profile to get started.');
      } else {
        console.error('❌ Unexpected response format:', result);
        setError('Unable to load profile data. Please try refreshing the page.');
      }
    } catch (error) {
      console.error('❌ Error loading profile:', error);
      setError('Failed to load profile data. Please check your connection and try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = s3PhotoService.validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setPhotoUploading(true);
    setError('');

    try {
      const result = await s3PhotoService.uploadProfilePhoto(user.userId, file);
      
      if (result.success) {
        setProfilePhoto(result.photoUrl);
        await userService.updateUser(user.userId, {
          profilePhotoUrl: result.photoUrl
        });
        setSuccess('Profile photo updated successfully!');
      } else {
        setError('Failed to upload photo');
      }
    } catch (error) {
      setError('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    setPhotoUploading(true);
    
    try {
      await s3PhotoService.deleteProfilePhoto(user.userId);
      await userService.updateUser(user.userId, {
        profilePhotoUrl: null
      });
      
      setProfilePhoto(null);
      setSuccess('Profile photo deleted successfully!');
    } catch (error) {
      setError('Failed to delete photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const profileData = {
        userId: user.userId,
        username: user.username,
        name: `${data.firstName} ${data.lastName}`.trim(),
        firstName: data.firstName,
        lastName: data.lastName,
        state: data.state || '',
        certifyingBody: data.certifyingBody,
        membershipType: data.membershipType || '',
        memberNumber: data.memberNumber || '',
        currentCycleEnd: data.currentCycleEnd,
        currentCycleStart: data.currentCycleStart || null,
        cycleEndDate: data.currentCycleEnd,
        profileCompleted: true,
        updatedAt: new Date().toISOString()
      };

      if (profilePhoto) {
        profileData.profilePhotoUrl = profilePhoto;
      }
      
      const result = await userService.saveUserProfile(profileData);
      
      if (result && result.success) {
        setSuccess('Profile updated successfully!');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setError('Failed to update profile');
      }
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600 mt-1">Manage your account information and certification details</p>
      </div>

      {error && <Alert variant="error" className="mb-6">{error}</Alert>}
      {success && <Alert variant="success" className="mb-6">{success}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Photo */}
        <div className="lg:col-span-1">
          <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Professional Badge</h2>
              <p className="text-sm text-gray-600">Your interpreter profile</p>
            </div>
            
            <div className="flex flex-col items-center">
              {/* Badge-style photo container */}
              <div className="relative bg-white p-4 rounded-2xl shadow-lg">
                {/* App branding */}
                <div className="text-center mb-3">
                  <h3 className="text-xl font-bold bg-gradient-hero bg-clip-text text-transparent">
                    CertifiAble
                  </h3>
                </div>
                
                <div className="relative">
                  {profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt={`${watch('firstName') || 'User'}'s profile photo`}
                      className="w-32 h-32 rounded-xl object-cover border-4 border-primary/20"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border-4 border-primary/20">
                      <User className="w-16 h-16 text-primary" />
                    </div>
                  )}
                  
                  {photoUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                </div>
                
                {/* Badge info */}
                <div className="mt-3 text-center">
                  <p className="font-semibold text-gray-900">
                    {watch('firstName') || 'Your'} {watch('lastName') || 'Name'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {watch('certifyingBody') || 'RID'} Certified Interpreter
                  </p>
                  {watch('membershipType') && (
                    <p className="text-xs text-primary font-medium mt-0.5">
                      {watch('membershipType')}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={photoUploading}
                  />
                  <span className={clsx(
                    "inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                    "bg-primary text-white hover:bg-primary-dark",
                    photoUploading && "opacity-50 cursor-not-allowed"
                  )}>
                    <Camera className="w-4 h-4 mr-2" />
                    {profilePhoto ? 'Change Photo' : 'Upload Photo'}
                  </span>
                </label>
                
                {profilePhoto && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeletePhoto}
                    disabled={photoUploading}
                    className="text-error"
                    aria-label="Delete profile photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-3 text-center">
                JPG, PNG or GIF. Max 5MB.
              </p>
            </div>
          </Card>
        </div>

        {/* Profile Form and State Licenses */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="First Name"
                      error={errors.firstName?.message}
                      required
                    />
                  )}
                />
                
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="Last Name"
                      error={errors.lastName?.message}
                      required
                    />
                  )}
                />
              </div>

              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="State"
                    options={[
                      { value: '', label: 'Select a state' },
                      ...US_STATES.map(state => ({ value: state, label: state }))
                    ]}
                  />
                )}
              />

              <div className="border-t border-gray-200 pt-4 mt-6">
                <h3 className="text-md font-semibold text-gray-900 mb-4">Certification Details</h3>
                
                <div className="space-y-4">
                  <Controller
                    name="certifyingBody"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Certifying Body"
                        options={[
                          { value: 'RID', label: 'RID (Registry of Interpreters for the Deaf)' },
                          { value: 'BEI', label: 'BEI (Board for Evaluation of Interpreters)' }
                        ]}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="membershipType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Certification Type"
                        options={[
                          { value: '', label: 'Select certification type' },
                          ...(MEMBERSHIP_TYPES[watchedCertifyingBody] || []).map(type => ({
                            value: type,
                            label: type
                          }))
                        ]}
                      />
                    )}
                  />

                  <Controller
                    name="memberNumber"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label="Member Number"
                        placeholder="Optional"
                      />
                    )}
                  />

                  <Controller
                    name="currentCycleStart"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="date"
                        label="Cycle Start Date"
                        helperText="Typically Jan 1 of cycle year. Edit if you have an extension."
                        error={errors.currentCycleStart?.message}
                      />
                    )}
                  />

                  <Controller
                    name="currentCycleEnd"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="date"
                        label="CEU Cycle End Date"
                        error={errors.currentCycleEnd?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Saving...' : 'Save Profile'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>

          {/* State Licenses - Integrated Design */}
          <StateLicensesCard userId={user?.userId} />
        </div>
      </div>
    </div>
  );
}
